<script setup>
// Ekran TV: wyniki na telewizorze albo dużym monitorze — results.sledzgps.pl/tv/{alias}?parametry.
// Makiety: kanwa „Wyniki – makiety”, rząd „Ekran TV” (TV1, TV2). Zadanie w Asanie: 1220178266920903.
//
// Kolumny „gazetowo” (w dół, potem następna kolumna). Gdy wszystko się mieści — nic się nie przewija,
// a litery rosną. Gdy nie — ostatnia kolumna: „Właśnie na mecie” (do 3 osób z ostatnich 10 min) i strony
// co 8 s (albo płynne przewijanie). Kilka tras (trasy=…) — zmiana trasy po pełnym obiegu stron.
// Parametry: utils/tv-params.js. Układ: utils/tv-layout.js (raz na pobranie i zmianę rozmiaru ekranu).
// Co sekundę zmienia się tylko zegar w nagłówku; strony przełącza ten sam licznik.
import { computed, onBeforeUnmount, onMounted, reactive, ref, shallowRef, toRaw, watch } from 'vue'
import { useRoute } from 'vue-router'
import qrcode from 'qrcode-generator'
import TvRow from './tv/TvRow.vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { buildResultsRows } from '../utils/results-list.js'
import { eventDateText, eventLogoUrl, eventPhase, formatHourMinute, normalizePartners, routeLabels } from '../utils/event-info.js'
import { formatClock, parseApiTime } from '../utils/polish-time.js'
import { useNowTick } from '../utils/now-tick.js'
import { parseTvParams } from '../utils/tv-params.js'
import {
  PAGE_MS, ROUTE_MIN_MS, SMOOTH_PAUSE_S, SMOOTH_SPEED,
  buildTvItems, columnLabel, fitLayout, isFresh, recentFinishers, splitColumns
} from '../utils/tv-layout.js'

const REFRESH_MS = 30000
const STALE_MS = 90 * 1000 // jak w nagłówku /{alias}: dane starsze w trakcie wydarzenia = „Brak połączenia”
const IDLE_MS = 3000 // kursor i przyciski znikają po 3 s bez ruchu myszą

const route = useRoute()
const resultsStore = useResultsStore()
const params = parseTvParams(route.query, String(route.params.eventAlias || ''))
const now = useNowTick()

// ---------- dane: osobno dla każdej trasy ----------
// alias → { event, sponsors, rows, hasPoints, catMode, phase, lastUpdate }
const data = shallowRef({})
const activeIndex = ref(0)
const alias = computed(() => params.aliases[activeIndex.value] || '')
const current = computed(() => data.value[alias.value] || null)
const firstLoadDone = ref(false)

// Magazyn wyników jest jeden — trasy pobierane po kolei: najpierw /events/alias, potem /splits
// (zawodnik bez start_time liczy czas od startu wydarzenia).
let queue = Promise.resolve()
let stopped = false

function load(target) {
  queue = queue
    .then(() => loadNow(target))
    .catch(error => console.warn('Ekran TV: nie udało się pobrać wyników', error))
  return queue
}

async function loadNow(target) {
  if (stopped || !target) return
  await resultsStore.fetchEventDetails(target)
  const payload = resultsStore.event
  const ev = payload?.event
  if (!ev || (ev.alias && ev.alias !== target)) return
  const before = toRaw(resultsStore.results)
  await resultsStore.fetchResults(target)
  const list = toRaw(resultsStore.results)
  if (stopped || list === before) return // błąd pobrania — zostają poprzednie dane (po 90 s „Brak połączenia”)
  const hasPoints = !!resultsStore.shouldShowPointsColumn()
  const rows = buildResultsRows(list, {
    categories: toRaw(resultsStore.categories) || [],
    eventStartMs: parseApiTime(ev.date_start),
    hasPoints
  })
  const catMode = rows.some(r => r.categoryName) ? 'category' : rows.some(r => r.gender) ? 'gender' : 'none'
  data.value = {
    ...data.value,
    [target]: { event: ev, sponsors: payload.sponsors || [], rows, hasPoints, catMode, phase: eventPhase(ev, Date.now()), lastUpdate: Date.now() }
  }
}

// ---------- ekran ----------
const viewport = ref({ width: window.innerWidth, height: window.innerHeight })
let resizeFrame = null
function onResize() {
  if (resizeFrame) return
  resizeFrame = requestAnimationFrame(() => {
    resizeFrame = null
    viewport.value = { width: window.innerWidth, height: window.innerHeight }
  })
}

const phase = computed(() => current.value?.phase || null)
const built = computed(() => (current.value ? buildTvItems(current.value.rows, current.value.phase) : { items: [], counts: { finished: 0, course: 0, dnf: 0, dns: 0 } }))
const layout = computed(() => fitLayout({ ...viewport.value, count: built.value.items.length, rows: params.rows }))

// „Właśnie na mecie” i podświetlenie świeżych — liczone przy zmianie strony i po pobraniu, nie co sekundę
const markNow = ref(Date.now())
const pinned = shallowRef([])
function updatePinned() {
  const c = current.value
  const next = c && c.phase === 'live' && !layout.value.fits ? recentFinishers(c.rows, markNow.value) : []
  const same = next.length === pinned.value.length && next.every((r, i) => r === pinned.value[i])
  if (!same) pinned.value = next
}
watch([current, () => layout.value.fits], () => {
  markNow.value = Date.now()
  updatePinned()
})

const split = computed(() => splitColumns(built.value.items, layout.value, pinned.value.length))
const page = ref(0)
const pageCount = computed(() => split.value.pages.length)
watch(pageCount, n => { if (page.value >= n) page.value = 0 })
const pageItems = computed(() => split.value.pages[page.value] || [])
const restLabel = computed(() => {
  if (params.scroll === 'smooth') return columnLabel(split.value.rest, phase.value)
  return columnLabel(pageItems.value, phase.value)
})
const windowHeight = computed(() => split.value.perPage * layout.value.rowHeight)

function freshOf(item) {
  return !!item.row && isFresh(item.row, markNow.value)
}

const catHead = computed(() => {
  const mode = current.value?.catMode
  return mode === 'category' ? 'Kat.' : mode === 'gender' ? 'K / M' : ''
})
const timeHead = computed(() => (current.value?.hasPoints ? 'Punkty' : phase.value === 'before' ? 'Start' : 'Czas'))

// ekran pionowy albo prawie kwadratowy: węższy nagłówek i stopka
const narrow = computed(() => viewport.value.width / viewport.value.height < 1.45)

const rootStyle = computed(() => ({
  '--tv-row': `${layout.value.rowHeight}px`,
  '--tv-s': String(layout.value.scale),
  '--tv-cols': String(layout.value.columns)
}))

// ---------- przewijanie, kilka tras, pauza ----------
const paused = ref(false)
const smoothOffset = ref(0)
const smoothMoving = ref(false)
let secondsOnPage = 0
let secondsOnRoute = 0
let smoothSeconds = 0
let switching = false

function resetPaging() {
  page.value = 0
  secondsOnPage = 0
  smoothSeconds = 0
  smoothMoving.value = false
  smoothOffset.value = 0
}

async function nextRoute() {
  if (switching || params.aliases.length < 2) return
  switching = true
  secondsOnRoute = 0
  const nextIndex = (activeIndex.value + 1) % params.aliases.length
  const target = params.aliases[nextIndex]
  const entry = data.value[target]
  if (!entry || Date.now() - entry.lastUpdate > REFRESH_MS) await load(target)
  switching = false
  if (stopped || !data.value[target]) return // nie udało się — zostajemy na tej trasie
  activeIndex.value = nextIndex
  resetPaging()
}

function cycleDone() {
  if (params.aliases.length > 1 && secondsOnRoute * 1000 >= ROUTE_MIN_MS) nextRoute()
}

function smoothStep() {
  const maxOffset = Math.max(0, split.value.rest.length * layout.value.rowHeight - windowHeight.value)
  if (!maxOffset) {
    smoothOffset.value = 0
    smoothMoving.value = false
    if (++smoothSeconds * 1000 >= PAGE_MS) {
      smoothSeconds = 0
      cycleDone()
    }
    return
  }
  smoothSeconds += 1
  const travel = smoothSeconds - SMOOTH_PAUSE_S
  const travelSeconds = maxOffset / (SMOOTH_SPEED * layout.value.scale)
  if (travel <= 0) {
    smoothMoving.value = false
    smoothOffset.value = 0
  } else if (travel <= Math.ceil(travelSeconds)) {
    smoothMoving.value = true
    smoothOffset.value = Math.min(maxOffset, travel * SMOOTH_SPEED * layout.value.scale)
  } else if (travel > Math.ceil(travelSeconds) + SMOOTH_PAUSE_S) {
    // koniec listy: postój, potem skok na początek
    smoothSeconds = 0
    smoothMoving.value = false
    smoothOffset.value = 0
    markNow.value = Date.now()
    updatePinned()
    cycleDone()
  }
}

function onTick() {
  if (paused.value || !current.value) return
  secondsOnRoute += 1
  if (params.scroll === 'smooth') {
    smoothStep()
    return
  }
  secondsOnPage += 1
  if (secondsOnPage * 1000 < PAGE_MS) return
  secondsOnPage = 0
  markNow.value = Date.now()
  updatePinned()
  if (pageCount.value > 1) {
    page.value = (page.value + 1) % pageCount.value
    if (page.value === 0) cycleDone()
  } else {
    cycleDone()
  }
}

function togglePause() {
  paused.value = !paused.value
}

// ---------- telewizor bez obsługi: kursor, pełny ekran, wygaszanie ----------
const idle = ref(false)
let idleTimer = null
function wake() {
  idle.value = false
  clearTimeout(idleTimer)
  idleTimer = setTimeout(() => { idle.value = true }, IDLE_MS)
}

const isFullscreen = ref(false)
const canFullscreen = !!document.documentElement.requestFullscreen
function toggleFullscreen() {
  if (document.fullscreenElement) document.exitFullscreen?.()
  else document.documentElement.requestFullscreen?.().catch(() => {})
}
function onFullscreenChange() {
  isFullscreen.value = !!document.fullscreenElement
}

function onKeydown(e) {
  if (e.key === ' ' || e.code === 'Space') {
    e.preventDefault()
    togglePause()
  } else if (e.key === 'f' || e.key === 'F') {
    toggleFullscreen()
  }
  wake()
}

let wakeLock = null
async function keepAwake() {
  if (wakeLock || !('wakeLock' in navigator) || document.visibilityState !== 'visible') return
  try {
    wakeLock = await navigator.wakeLock.request('screen')
    wakeLock.addEventListener('release', () => { wakeLock = null })
  } catch (error) {
    wakeLock = null // przeglądarka TV albo oszczędzanie baterii — ekran może wygasnąć według ustawień TV
  }
}
function onVisibility() {
  if (document.visibilityState === 'visible') {
    keepAwake()
    load(alias.value)
  }
}

// ---------- nagłówek i stopka ----------
const event = computed(() => current.value?.event || null)
const eventName = computed(() => event.value?.name || alias.value)
const dateText = computed(() => eventDateText(event.value))
const logoFailed = reactive({})
const logoUrl = computed(() => {
  const url = eventLogoUrl(event.value)
  return url && !logoFailed[url] ? url : ''
})
const clock = computed(() => formatHourMinute(now.value))
const stale = computed(() => phase.value === 'live' && current.value?.lastUpdate != null && now.value - current.value.lastUpdate > STALE_MS)
const updatedAt = computed(() => (current.value?.lastUpdate != null ? formatClock(current.value.lastUpdate) : ''))
const counts = computed(() => built.value.counts)

const routeChip = computed(() => {
  if (params.aliases.length < 2) return ''
  const loaded = params.aliases.filter(a => data.value[a])
  const labels = routeLabels(loaded.map(a => data.value[a].event))
  const label = labels[loaded.indexOf(alias.value)] || ''
  return `${label ? `${label} · ` : ''}trasa ${activeIndex.value + 1}/${params.aliases.length}`
})

const failedPartners = reactive({})
const partners = computed(() => normalizePartners(current.value?.sponsors).filter(p => !failedPartners[p.key]))
const brandLogo = `${process.env.BASE_URL || '/'}favicon.svg`

// Kod QR do zwykłej strony wyników tej trasy (kibic przy ekranie otwiera ją w telefonie)
const qr = computed(() => {
  if (!alias.value) return null
  try {
    const url = `${window.location.origin}/${encodeURIComponent(alias.value)}`
    const code = qrcode(0, 'M')
    code.addData(url)
    code.make()
    const size = code.getModuleCount()
    let d = ''
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) if (code.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`
    }
    return { size, d }
  } catch (error) {
    return null
  }
})

// ---------- start i sprzątanie ----------
let tickTimer = null
let refreshTimer = null

onMounted(async () => {
  document.documentElement.classList.add('tv-page')
  window.addEventListener('resize', onResize)
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('mousemove', wake)
  document.addEventListener('pointerdown', wake)
  document.addEventListener('fullscreenchange', onFullscreenChange)
  document.addEventListener('visibilitychange', onVisibility)
  wake()
  keepAwake()
  tickTimer = setInterval(onTick, 1000)
  if (!params.aliases.length) {
    firstLoadDone.value = true
    return
  }
  await load(params.aliases[0])
  firstLoadDone.value = true
  // pozostałe trasy w tle, żeby zmiana trasy była od razu
  params.aliases.slice(1).forEach(a => load(a))
  // odświeżanie tylko trasy na ekranie
  refreshTimer = setInterval(() => load(alias.value), REFRESH_MS)
})

onBeforeUnmount(() => {
  stopped = true
  document.documentElement.classList.remove('tv-page')
  window.removeEventListener('resize', onResize)
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('mousemove', wake)
  document.removeEventListener('pointerdown', wake)
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  document.removeEventListener('visibilitychange', onVisibility)
  clearInterval(tickTimer)
  clearInterval(refreshTimer)
  clearTimeout(idleTimer)
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  if (wakeLock) wakeLock.release().catch(() => {})
})
</script>

<template>
  <div class="tv" :class="{ 'tv--idle': idle && !paused, 'tv--narrow': narrow }" :style="rootStyle">
    <header class="tv-head">
      <span v-if="logoUrl" class="tv-head__logo">
        <img :src="logoUrl" :alt="`Logo: ${eventName}`" @error="logoFailed[logoUrl] = true" />
      </span>
      <div class="tv-head__text">
        <h1 class="tv-head__name">{{ eventName }}</h1>
        <p class="tv-head__sub">
          <span v-if="routeChip" class="tv-head__route">{{ routeChip }}</span>
          <span class="tv-head__date">{{ dateText }}</span>
        </p>
      </div>
      <div v-if="phase" class="tv-head__state">
        <span v-if="phase === 'live' && stale" class="tv-chip tv-chip--warn"><span class="tv-chip__dot" aria-hidden="true" />Brak połączenia</span>
        <span v-else-if="phase === 'live'" class="tv-chip tv-chip--live"><span class="tv-chip__dot" aria-hidden="true" />Na żywo</span>
        <span v-else-if="phase === 'before'" class="tv-chip tv-chip--before"><span class="tv-chip__dot" aria-hidden="true" />Przed startem</span>
        <span v-else class="tv-chip tv-chip--ended"><span class="tv-chip__dot" aria-hidden="true" />Zakończone</span>
        <span v-if="updatedAt" class="tv-head__updated">{{ stale ? 'dane z' : 'akt.' }} {{ updatedAt }}</span>
      </div>
      <div v-if="current" class="tv-head__stats">
        <template v-if="phase === 'before'">
          <span class="tv-stat"><strong>{{ counts.course }}</strong><span>na liście</span></span>
        </template>
        <template v-else>
          <span class="tv-stat"><strong>{{ counts.finished }}</strong><span>na mecie</span></span>
          <span v-if="phase === 'live'" class="tv-stat tv-stat--course"><strong>{{ counts.course }}</strong><span>w trasie</span></span>
          <span class="tv-stat tv-stat--dnf"><strong>{{ counts.dnf }}</strong><span>DNF</span></span>
        </template>
      </div>
      <span class="tv-head__clock">{{ clock }}</span>
      <span v-if="qr" class="tv-head__qr">
        <span class="tv-head__qr-text">Wyniki<br />w telefonie</span>
        <svg :viewBox="`-4 -4 ${qr.size + 8} ${qr.size + 8}`" role="img" aria-label="Kod QR do wyników w telefonie" shape-rendering="crispEdges">
          <rect class="tv-qr__bg" x="-4" y="-4" :width="qr.size + 8" :height="qr.size + 8" />
          <path class="tv-qr__dots" :d="qr.d" />
        </svg>
      </span>
    </header>

    <main class="tv-main" :class="{ 'tv-main--fits': layout.fits }">
      <p v-if="!params.aliases.length" class="tv-empty">Nieprawidłowy adres ekranu wyników.</p>
      <p v-else-if="!current" class="tv-empty">{{ firstLoadDone ? 'Nie udało się wczytać wyników. Spróbujemy ponownie za chwilę.' : 'Wczytywanie wyników…' }}</p>
      <p v-else-if="!built.items.length" class="tv-empty">Brak zawodników na liście.</p>
      <template v-else>
        <section v-for="(chunk, index) in split.fixed" :key="`c${index}`" class="tv-col">
          <div class="tv-col__head">
            <span class="tv-col__label">{{ columnLabel(chunk, phase) }}</span>
            <span v-if="catHead" class="tv-col__cat">{{ catHead }}</span>
            <span class="tv-col__time">{{ timeHead }}</span>
          </div>
          <ol class="tv-list">
            <TvRow v-for="item in chunk" :key="item.key" :item="item" :surname-first="params.surnameFirst" :cat-mode="current.catMode" :has-points="current.hasPoints" :phase="phase" :fresh="freshOf(item)" />
          </ol>
        </section>

        <section v-if="split.pages.length" class="tv-col tv-col--rest">
          <div class="tv-col__head">
            <span class="tv-col__label">{{ restLabel }}</span>
            <span v-if="params.scroll === 'pages' && pageCount > 1" class="tv-pages" :aria-label="`Strona ${page + 1} z ${pageCount}`">
              <span v-if="pageCount <= 12" class="tv-pages__dots" aria-hidden="true">
                <span v-for="n in pageCount" :key="n" :class="{ 'is-on': n - 1 === page }" />
              </span>
              <span class="tv-pages__num">{{ page + 1 }}/{{ pageCount }}</span>
            </span>
          </div>
          <div v-if="pinned.length" class="tv-recent">
            <div class="tv-recent__head">
              <span class="tv-recent__label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 21V4" /><path d="M5 4h12l-2.5 4L17 12H5" /></svg>
                Właśnie na mecie
              </span>
              <span class="tv-col__cat">meta</span>
              <span class="tv-col__time">{{ current.hasPoints ? 'punkty' : 'czas' }}</span>
            </div>
            <ol class="tv-list">
              <TvRow v-for="row in pinned" :key="`p${row.id}`" :item="{ key: `p${row.id}`, row }" :surname-first="params.surnameFirst" :cat-mode="current.catMode" :has-points="current.hasPoints" :phase="phase" finish-clock />
            </ol>
          </div>
          <div class="tv-window" :style="{ height: `${windowHeight}px` }">
            <ol
              v-if="params.scroll === 'smooth'"
              class="tv-list"
              :style="{ transform: `translateY(${-smoothOffset}px)`, transition: smoothMoving ? 'transform 1s linear' : 'none' }"
            >
              <TvRow v-for="item in split.rest" :key="item.key" :item="item" :surname-first="params.surnameFirst" :cat-mode="current.catMode" :has-points="current.hasPoints" :phase="phase" :fresh="freshOf(item)" />
            </ol>
            <Transition v-else name="tv-fade" mode="out-in">
              <ol :key="`${alias}-${page}`" class="tv-list">
                <TvRow v-for="item in pageItems" :key="item.key" :item="item" :surname-first="params.surnameFirst" :cat-mode="current.catMode" :has-points="current.hasPoints" :phase="phase" :fresh="freshOf(item)" />
              </ol>
            </Transition>
          </div>
        </section>
      </template>
    </main>

    <footer class="tv-foot">
      <template v-if="partners.length">
        <span class="tv-foot__title">Partnerzy wydarzenia</span>
        <span v-for="p in partners" :key="p.key" class="tv-foot__tile" :title="p.name || undefined">
          <img :src="p.src" :alt="p.name || 'Partner wydarzenia'" @error="failedPartners[p.key] = true" />
        </span>
      </template>
      <span class="tv-foot__spacer" />
      <span class="tv-foot__brand"><span class="tv-foot__brand-text">Pomiar czasu i śledzenie GPS:</span><img :src="brandLogo" alt="" /><strong>ŚledźGPS</strong></span>
    </footer>

    <div v-if="paused" class="tv-paused" role="status">Pauza · spacja wznawia</div>
    <div class="tv-controls" :class="{ 'is-hidden': idle }">
      <button type="button" class="tv-btn" @click="togglePause">{{ paused ? 'Wznów' : 'Pauza' }}</button>
      <button v-if="canFullscreen" type="button" class="tv-btn" @click="toggleFullscreen">{{ isFullscreen ? 'Zamknij pełny ekran' : 'Pełny ekran' }}</button>
    </div>
  </div>
</template>

<style>
/* Ekran TV zajmuje całe okno: bez marginesu #app z App.vue i bez przewijania strony */
html.tv-page,
html.tv-page body {
  height: 100%;
  overflow: hidden;
}
html.tv-page #app {
  margin: 0;
  text-align: left;
}
</style>

<style scoped>
/* Kolory tylko z palety (--sledz-*). Jaśniejsze odcienie przez color-mix z koloru palety; deklaracja
   przed nim to zapas dla przeglądarek bez color-mix (starsze telewizory). */
.tv {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: var(--sledz-surface-alt);
  color: var(--sledz-text);
  font-family: Lato, Avenir, Helvetica, Arial, sans-serif;
  text-align: left;
}
.tv--idle {
  cursor: none;
}

/* ---------- nagłówek ---------- */
.tv-head {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: calc(88px * var(--tv-s));
  padding: 0 calc(32px * var(--tv-s));
  display: flex;
  align-items: center;
  gap: calc(22px * var(--tv-s));
  background: var(--sledz-surface);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: calc(17px * var(--tv-s));
}
.tv-head__logo {
  flex: none;
  width: calc(64px * var(--tv-s));
  height: calc(64px * var(--tv-s));
  padding: calc(5px * var(--tv-s));
  border-radius: calc(12px * var(--tv-s));
  border: 1px solid var(--sledz-border-soft);
  background: var(--sledz-surface);
  display: flex;
  align-items: center;
  justify-content: center;
}
.tv-head__logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.tv-head__text {
  flex: 1;
  min-width: 0;
}
.tv-head__name {
  margin: 0;
  font-size: calc(30px * var(--tv-s));
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tv-head__sub {
  margin: calc(5px * var(--tv-s)) 0 0;
  display: flex;
  align-items: center;
  gap: calc(10px * var(--tv-s));
  color: var(--sledz-text-secondary);
  white-space: nowrap;
  overflow: hidden;
}
.tv-head__date {
  overflow: hidden;
  text-overflow: ellipsis;
}
.tv-head__route {
  flex: none;
  padding: 0.1em 0.6em;
  border-radius: 1em;
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
  font-weight: 800;
}
.tv-head__state {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: calc(5px * var(--tv-s));
}
.tv-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  height: calc(34px * var(--tv-s));
  padding: 0 0.8em;
  border-radius: calc(17px * var(--tv-s));
  font-size: calc(18px * var(--tv-s));
  font-weight: 800;
  white-space: nowrap;
}
.tv-chip__dot {
  width: 0.5em;
  height: 0.5em;
  border-radius: 50%;
  background: currentColor;
}
.tv-chip--live {
  background: var(--sledz-surface-alt);
  background: color-mix(in srgb, var(--sledz-live) 14%, var(--sledz-surface));
  color: var(--sledz-live-strong);
}
.tv-chip--live .tv-chip__dot {
  background: var(--sledz-live);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--sledz-live) 18%, transparent);
}
.tv-chip--before {
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.tv-chip--ended {
  background: var(--sledz-border-soft);
  color: var(--sledz-text-secondary);
}
.tv-chip--ended .tv-chip__dot {
  background: var(--sledz-text-muted);
}
.tv-chip--warn {
  background: var(--sledz-warning-soft);
  color: var(--sledz-warning-ink);
}
.tv-chip--warn .tv-chip__dot {
  background: var(--sledz-warning);
}
.tv-head__updated {
  font-size: calc(14px * var(--tv-s));
  color: var(--sledz-text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.tv-head__stats {
  flex: none;
  height: calc(58px * var(--tv-s));
  padding: 0 calc(24px * var(--tv-s));
  border-left: 1px solid var(--sledz-border-soft);
  border-right: 1px solid var(--sledz-border-soft);
  display: flex;
  align-items: center;
  gap: calc(26px * var(--tv-s));
}
.tv-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
}
.tv-stat strong {
  font-size: calc(30px * var(--tv-s));
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}
.tv-stat span {
  margin-top: calc(6px * var(--tv-s));
  font-size: calc(14px * var(--tv-s));
  font-weight: 700;
  color: var(--sledz-text-muted);
  white-space: nowrap;
}
.tv-stat--course strong {
  color: var(--sledz-primary-deep);
}
.tv-stat--dnf strong {
  color: var(--sledz-live-strong);
}
.tv-head__clock {
  flex: none;
  font-size: calc(56px * var(--tv-s));
  font-weight: 900;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.tv-head__qr {
  flex: none;
  display: flex;
  align-items: center;
  gap: calc(12px * var(--tv-s));
}
.tv-head__qr-text {
  text-align: right;
  font-size: calc(15px * var(--tv-s));
  line-height: 1.3;
  color: var(--sledz-text-secondary);
}
.tv-qr__bg {
  fill: var(--sledz-surface);
}
.tv-qr__dots {
  fill: var(--sledz-text);
}
.tv-head__qr svg {
  width: calc(76px * var(--tv-s));
  height: calc(76px * var(--tv-s));
  border-radius: calc(6px * var(--tv-s));
  border: 1px solid var(--sledz-border-soft);
}

/* ---------- kolumny ---------- */
.tv-main {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(88px * var(--tv-s));
  bottom: calc(56px * var(--tv-s));
  padding: calc(14px * var(--tv-s)) calc(28px * var(--tv-s));
  display: flex;
  justify-content: center;
  gap: calc(14px * var(--tv-s));
}
.tv-col {
  flex: 1 1 0;
  min-width: 0;
  max-width: calc(var(--tv-row) * 16);
  display: flex;
  flex-direction: column;
  background: var(--sledz-surface);
  border: 1px solid var(--sledz-border-soft);
  border-radius: calc(12px * var(--tv-s));
  overflow: hidden;
}
.tv-main--fits .tv-col {
  align-self: flex-start;
}
.tv-col__head,
.tv-recent__head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 0.47em;
  padding: 0 0.6em;
  font-size: calc(var(--tv-row) * 0.5);
  white-space: nowrap;
}
.tv-col__head {
  height: calc(36px * var(--tv-s));
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.tv-col__head > *,
.tv-recent__head > * {
  font-size: 0.78em;
}
.tv-col__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--sledz-text);
  font-size: 0.9em !important;
}
.tv-col__cat {
  flex: none;
  width: calc(3.87em / 0.78);
}
.tv-col__time {
  flex: none;
  width: calc(4.93em / 0.78);
  text-align: right;
}
.tv-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.tv-pages {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  text-transform: none;
  letter-spacing: 0;
  color: var(--sledz-text-secondary);
}
.tv-pages__dots {
  display: inline-flex;
  gap: 0.3em;
}
.tv-pages__dots span {
  width: 0.55em;
  height: 0.55em;
  border-radius: 50%;
  background: var(--sledz-border);
}
.tv-pages__dots span.is-on {
  background: var(--sledz-primary-deep);
}
.tv-recent {
  flex: none;
  background: var(--sledz-primary-soft);
  border-bottom: 2px solid var(--sledz-primary);
}
.tv-recent__head {
  height: var(--tv-row);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sledz-text-secondary);
}
.tv-recent__label {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.4em;
  color: var(--sledz-primary-deep);
}
.tv-recent__label svg {
  width: 1.1em;
  height: 1.1em;
  flex: none;
}
.tv-recent :deep(.tvr) {
  background: transparent;
  border-bottom-color: var(--sledz-border-soft);
  border-bottom-color: color-mix(in srgb, var(--sledz-primary-deep) 15%, transparent);
}
.tv-window {
  flex: none;
  overflow: hidden;
}
.tv-window .tv-list {
  will-change: transform;
}
.tv-fade-enter-active,
.tv-fade-leave-active {
  transition: opacity 0.25s ease;
}
.tv-fade-enter-from,
.tv-fade-leave-to {
  opacity: 0;
}
.tv-empty {
  align-self: center;
  margin: 0;
  font-size: calc(28px * var(--tv-s));
  color: var(--sledz-text-secondary);
}

/* ---------- stopka ---------- */
.tv-foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(56px * var(--tv-s));
  padding: 0 calc(32px * var(--tv-s));
  display: flex;
  align-items: center;
  gap: calc(10px * var(--tv-s));
  background: var(--sledz-surface);
  border-top: 1px solid var(--sledz-border-soft);
  font-size: calc(14px * var(--tv-s));
  color: var(--sledz-text-muted);
}
.tv-foot__title {
  flex: none;
  width: calc(92px * var(--tv-s));
  font-size: calc(12px * var(--tv-s));
  line-height: 1.25;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.tv-foot__tile {
  flex: 0 1 calc(150px * var(--tv-s));
  min-width: calc(64px * var(--tv-s));
  height: calc(42px * var(--tv-s));
  padding: calc(4px * var(--tv-s)) calc(8px * var(--tv-s));
  border-radius: calc(10px * var(--tv-s));
  border: 1px solid var(--sledz-border-soft);
  background: var(--sledz-surface);
  display: flex;
  align-items: center;
  justify-content: center;
}
.tv-foot__tile img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.tv-foot__spacer {
  flex: 1;
}
.tv-foot__brand {
  flex: none;
  display: flex;
  align-items: center;
  gap: 0.5em;
  white-space: nowrap;
}
.tv-foot__brand img {
  width: calc(22px * var(--tv-s));
  height: calc(22px * var(--tv-s));
  border-radius: 4px;
}
.tv-foot__brand strong {
  color: var(--sledz-text);
}

/* ekran pionowy: nazwa wydarzenia ma pierwszeństwo przed dopiskami */
.tv--narrow .tv-head {
  gap: calc(14px * var(--tv-s));
  padding: 0 calc(20px * var(--tv-s));
}
.tv--narrow .tv-head__qr-text,
.tv--narrow .tv-foot__brand-text,
.tv--narrow .tv-foot__title {
  display: none;
}
.tv--narrow .tv-head__stats {
  padding: 0 calc(14px * var(--tv-s));
  gap: calc(16px * var(--tv-s));
}
.tv--narrow .tv-head__clock {
  font-size: calc(40px * var(--tv-s));
}
.tv--narrow .tv-head__name {
  font-size: calc(26px * var(--tv-s));
}

/* ---------- pauza i przyciski (po ruchu myszą) ---------- */
.tv-paused {
  position: absolute;
  left: 50%;
  bottom: calc(72px * var(--tv-s));
  transform: translateX(-50%);
  padding: 0.5em 1.1em;
  border-radius: 2em;
  background: var(--sledz-panel-deep);
  color: var(--sledz-panel-text);
  font-size: calc(18px * var(--tv-s));
  font-weight: 800;
}
.tv-controls {
  position: absolute;
  right: calc(32px * var(--tv-s));
  bottom: calc(72px * var(--tv-s));
  display: flex;
  gap: 8px;
  transition: opacity 0.3s ease;
}
.tv-controls.is-hidden {
  opacity: 0;
  pointer-events: none;
}
.tv-btn {
  min-height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: 22px;
  background: var(--sledz-panel-deep);
  color: var(--sledz-panel-text);
  font-size: 15px;
  font-weight: 800;
  box-shadow: 0 2px 8px color-mix(in srgb, var(--sledz-panel-deep) 30%, transparent);
}
.tv-btn:focus-visible {
  outline: 3px solid var(--sledz-primary);
  outline-offset: 2px;
}
</style>
